/**
 * The client-side board model.
 *
 * ## The central decision: pending mutations are an overlay, not an edit
 *
 * The obvious way to build optimistic updates is to apply the change to the
 * store immediately and remember the inverse so it can be undone. It works, and
 * it is where optimistic UIs go wrong: the inverse has to be correct for every
 * mutation kind, it has to compose when two mutations touch the same object, and
 * a rollback that races with an incoming server frame restores a value that was
 * never true.
 *
 * Slate does something different. `cards`, `columns` and `labels` hold *only*
 * what the server has confirmed. A mutation appends an entry to `pending`, and
 * the rendered board is `materialise(base, pending)` - a pure function that
 * replays the pending operations over the confirmed state. Rollback is then
 * `pending.filter(op => op.opId !== failed)` and nothing else. There is no
 * inverse to get wrong, and the confirmed state is never polluted by an
 * intention the server has not accepted.
 *
 * The cost is one extra pass over the cards on every render, which is a few
 * hundred microseconds for a large board and is memoised per pending list.
 *
 * ## What the overlay deliberately does not do
 *
 * It does not touch `version`. The optimistic card carries the version it was
 * built from, because the version is the server's token and inventing one
 * locally means the *next* write sends a version the server has never seen and
 * gets a 409 for a reason the user cannot understand.
 */

import type {
  Board,
  BoardEvent,
  Card,
  CardAttachment,
  CardComment,
  CardId,
  Column,
  ColumnId,
  ConnectionStatus,
  CursorPosition,
  Label,
  Presence,
  WorkspaceMember,
} from '@slate/shared';
import type { CardPatchBody } from '../api/types.js';

/** The subset of a card the drawer can edit. `version`, `position`, `id` and the
 * timestamps are absent on purpose: they are server-owned, and a patch type that
 * included them would invite a caller to set one.
 *
 * `labelIds` rather than resolved `Label` objects, because that is what the wire
 * carries on both transports - see `cardPatchSchema` in
 * `packages/shared/src/protocol.ts` and `updateCardBodySchema` in `http.ts`. The
 * overlay resolves them against the board's labels for rendering; storing the
 * resolved objects here would mean the optimistic card and the acknowledged card
 * disagreed about a label the user had just renamed.
 *
 * It is an alias of the shared schema's inferred type rather than a second
 * declaration of the same six fields. Both transports send this exact shape, so
 * a field added to the server's validator appears here - and therefore in every
 * caller of `updateCard` - as a compile error rather than as a silently ignored
 * field. */
export type CardPatch = CardPatchBody;

/**
 * A mutation that has been applied locally and not yet confirmed.
 *
 * `at` is a client timestamp used only to order the overlay and to show a
 * pending indicator that ages out; it is never sent anywhere.
 *
 * `revision` and `sentRevision` implement the dirty-flag guard for coalesced
 * edits - see `state/BoardProvider.tsx`, `commitCardUpdate`. `revision` counts
 * local merges into this operation; `sentRevision` records which revision the
 * last frame carried. Acknowledging an operation whose `revision` has moved past
 * `sentRevision` leaves it pending and the send loop runs again, which is what
 * makes typing in a title field produce one frame per round trip instead of one
 * frame per keystroke.
 */
export type PendingOp =
  | { opId: string; kind: 'card.create'; at: number; card: Card }
  | { opId: string; kind: 'card.move'; at: number; cardId: CardId; columnId: ColumnId; index: number }
  | {
      opId: string;
      kind: 'card.update';
      at: number;
      cardId: CardId;
      patch: CardPatch;
      revision: number;
      sentRevision: number;
    }
  | { opId: string; kind: 'card.archive'; at: number; cardId: CardId }
  | { opId: string; kind: 'card.delete'; at: number; cardId: CardId }
  | { opId: string; kind: 'comment.create'; at: number; cardId: CardId; comment: CardComment }
  | { opId: string; kind: 'column.create'; at: number; column: Column }
  | { opId: string; kind: 'column.reorder'; at: number; columnId: ColumnId; index: number };

export type PendingKind = PendingOp['kind'];

export interface TypingEntry {
  userId: string;
  name: string;
  at: number;
}

/** A toast. `tone` drives the colour; the copy is produced by the caller so the
 * reducer never has to know what a sentence looks like. */
export interface Notice {
  id: string;
  tone: 'info' | 'error' | 'success';
  message: string;
  /** Set for errors that carry a `requestId`, so a user can quote it and an
   * engineer can find the log line. */
  requestId?: string | null;
}

export interface BoardState {
  boardId: string | null;
  /** `loading` covers the first snapshot fetch; a reconnect does not return to
   * it, because the board is still renderable while reconnecting. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  /**
   * The server has definitively refused this board - 401, 403 or 404 - as opposed
   * to the load having failed for a reason that might resolve.
   *
   * The distinction exists because of how a browser reports a rejected WebSocket
   * upgrade: the handshake's HTTP status is not exposed to script, so a socket
   * opened for a board that does not exist closes with `1006`, which the reconnect
   * logic treats as the most retryable condition there is. Without this flag the
   * client would retry a board it will never be allowed to join, forever, at
   * thirty-second intervals. See `BoardProvider`'s socket effect.
   */
  boardUnavailable: boolean;

  board: Board | null;
  columns: Column[];
  /** Normalised by id. A board is a set of cards that happens to be ordered, and
   * an array would make every card update an O(n) scan plus a splice. */
  cards: Record<CardId, Card>;
  labels: Label[];
  members: WorkspaceMember[];

  /** The highest board event sequence applied locally. Sent as `since` on every
   * reconnect, and the thing the gap detector compares against. */
  sequence: number;

  pending: PendingOp[];

  connection: ConnectionStatus;
  clientId: string | null;
  presence: Record<string, Presence>;
  /** Keyed by card id, with `'*'` for a board-level indicator. */
  typing: Record<string, TypingEntry[]>;
  latencyMs: number | null;

  /** Loaded on demand when the drawer opens. Absent means "not fetched", which
   * is different from "empty" - the drawer shows a spinner for the first and
   * "no comments yet" for the second. */
  comments: Record<CardId, CardComment[]>;
  attachments: Record<CardId, CardAttachment[]>;
  history: Record<CardId, BoardEvent[]>;

  activity: BoardEvent[];
  activityCursor: string | null;
  /** Set when a live frame arrives that the feed does not yet contain. The
   * provider debounces a refetch of page one. See `BoardProvider`. */
  activityStale: boolean;

  /** Set when local state cannot be trusted and a fresh snapshot is the only
   * correct recovery: a rejected mutation with no authoritative row to fall back
   * on, or a `welcome` that reported `resyncRequired`. */
  needsResync: boolean;

  notices: Notice[];
}

export function initialBoardState(): BoardState {
  return {
    boardId: null,
    status: 'idle',
    error: null,
    boardUnavailable: false,
    board: null,
    columns: [],
    cards: {},
    labels: [],
    members: [],
    sequence: 0,
    pending: [],
    connection: 'offline',
    clientId: null,
    presence: {},
    typing: {},
    latencyMs: null,
    comments: {},
    attachments: {},
    history: {},
    activity: [],
    activityCursor: null,
    activityStale: false,
    needsResync: false,
    notices: [],
  };
}

/** A card's position within the rendered board, used by the drag preview and by
 * the drop resolver. */
export interface DropTarget {
  columnId: ColumnId;
  index: number;
}

export interface DragPreview extends DropTarget {
  cardId: CardId;
}

/** Presence is keyed per connection, not per user, because one account in two
 * tabs is two cursors. `Presence.clientId` is that connection id. */
export type PresenceMap = Record<string, Presence>;

/** The shape the cursor overlay consumes. Extracted so the overlay does not have
 * to know about presence bookkeeping. */
export interface RemoteCursor {
  clientId: string;
  userId: string;
  name: string;
  colour: string;
  position: CursorPosition;
}
